/**
 * weatherWindStreamlines.ts
 *
 * GPU-driven wind particle streamlines that stay glued to the map at every
 * zoom AND tilt.
 *
 * Implementation notes
 * --------------------
 * The previous version of this file rendered into its own overlay <canvas>
 * positioned above the map and built its own MVP matrix from
 * `map.project(lngLat)` results. That approach works for an untilted top-down
 * view because `project()` returns the correct 2D screen position, but it
 * breaks the moment the map is pitched: `project()` discards the depth/w
 * component of MapLibre's perspective projection, so particles end up in the
 * wrong place (or get clipped entirely) on a tilted view.
 *
 * The fix is to draw inside MapLibre's own GL context as a CustomLayerInterface
 * and use the projection matrix that MapLibre passes into `render()` — that
 * matrix bakes in tilt, rotation, and perspective for free. This is exactly
 * how `weatherRasterLayer.ts` stays pinned to the basemap when tilted.
 *
 * Particle simulation still happens in the wind field's UV space (Agafonkin
 * style), and the trail buffer is still a screen-space FBO that we clear on
 * every map move. Only the *drawing* changed: instead of a hand-rolled
 * mercator→clip MVP, we feed unit-mercator coords through MapLibre's matrix.
 */

import { MercatorCoordinate } from "maplibre-gl";
import type { Map as MaplibreMap, CustomLayerInterface } from "maplibre-gl";
import type { WeatherFrame } from "./weatherRasterLayer";

// ─── Shader sources ─────────────────────────────────────────────────────────

/**
 * DRAW vertex shader — positions each particle on the map using MapLibre's
 * own projection matrix. Particle UV [0,1]² is mapped into unit-mercator via
 * the wind field's bbox, then projected through u_mvp to clip space. Because
 * u_mvp comes straight from MapLibre, it includes any tilt/rotation.
 */
const DRAW_VERT = `
precision mediump float;

attribute float a_index;

uniform sampler2D u_particles;
uniform float u_particles_res;
uniform mat4 u_mvp;
uniform vec4 u_windBboxMerc; // (nw.x, nw.y, se.x, se.y) in unit-mercator
uniform vec2 u_view_min;     // visible viewport in data-UV space
uniform vec2 u_view_max;
// Fraction of particles to actually draw, 0..1. The simulation always runs a
// full screen of particles (viewport-space), which looks right when zoomed out
// but far too dense when zoomed in. Rather than rebuild with fewer particles,
// we deterministically cull a fraction here based on each particle's index, so
// the on-screen count genuinely drops as you zoom in. 1.0 = draw all.
uniform float u_draw_fraction;
uniform float u_point_size;

varying vec2 v_particle_pos; // data-UV of this particle (for wind-speed color)

// Cheap deterministic hash of the particle index → [0,1]. Stable per particle
// across frames so culled particles stay culled (no flicker) until the
// fraction changes.
float index_hash(float i) {
    return fract(sin(i * 12.9898) * 43758.5453);
}

void main() {
    // Cull particles above the draw fraction by sending them outside clip
    // space (they produce no fragment). Deterministic per index so the kept
    // set is stable frame to frame.
    if (index_hash(a_index) > u_draw_fraction) {
        gl_Position = vec4(2.0, 2.0, 2.0, 1.0); // offscreen
        gl_PointSize = 0.0;
        return;
    }

    vec4 color = texture2D(u_particles, vec2(
        fract(a_index / u_particles_res),
        floor(a_index / u_particles_res) / u_particles_res));

    // Decode the particle position. It is stored in VIEWPORT space ([0,1]^2 =
    // the visible map area), so first map it into data-UV via the view rect.
    vec2 viewPos = vec2(
        color.r / 255.0 + color.b,
        color.g / 255.0 + color.a);
    vec2 dataUv = mix(u_view_min, u_view_max, viewPos);

    // Expose data-UV to the fragment shader so it can look up wind speed for
    // coloring (the fragment shader samples u_wind at v_particle_pos).
    v_particle_pos = dataUv;

    // data-UV → unit-mercator via the wind bbox, then project with the map MVP.
    vec2 mercPos = mix(u_windBboxMerc.xy, u_windBboxMerc.zw, dataUv);

    gl_PointSize = u_point_size;
    gl_Position = u_mvp * vec4(mercPos, 0.0, 1.0);
}
`;

/** DRAW fragment shader — colors each particle by wind speed. */
const DRAW_FRAG = `
precision mediump float;

uniform sampler2D u_wind;
uniform vec2 u_wind_min;
uniform vec2 u_wind_max;
uniform sampler2D u_color_ramp;
// Multiplies only the RGB of the streamline color (alpha untouched). Driven by
// the basemap theme so the streamlines can be rendered darker on the dark map
// than on the light map (e.g. 1.0 on light, 0.9 = 10% darker on dark). Because
// the tail is just faded copies of past heads, tinting here carries into the
// tail automatically, so head and tail share the one brightness.
uniform float u_brightness;

varying vec2 v_particle_pos;

void main() {
    // Updated GFS pipeline quantization: byte = 1 + 254*t, byte 0 = NoData.
    // Dequantize with (byte-1)/254 so the encoding's zero point (byte 128)
    // lands exactly at t=0.5 → 0 m/s; plain byte/255 would read ~0.55 m/s of
    // phantom wind in calm air over the ±70 m/s range.
    vec2 raw = texture2D(u_wind, v_particle_pos).rg;
    vec2 t = clamp((raw * 255.0 - 1.0) / 254.0, 0.0, 1.0);
    vec2 velocity = mix(u_wind_min, u_wind_max, t);
    float speed_t = length(velocity) / length(u_wind_max);

    vec2 ramp_pos = vec2(
        fract(16.0 * speed_t),
        floor(16.0 * speed_t) / 16.0);

    vec4 c = texture2D(u_color_ramp, ramp_pos);
    gl_FragColor = vec4(c.rgb * u_brightness, c.a);
}
`;

/** Full-screen quad vertex shader (NDC, screen-space — used for the trail
 *  buffer composite, which is intentionally screen-space). */
const QUAD_VERT = `
precision mediump float;

attribute vec2 a_pos;

varying vec2 v_tex_pos;

void main() {
    v_tex_pos = a_pos;
    gl_Position = vec4(1.0 - 2.0 * a_pos, 0, 1);
}
`;

/** Screen compositing fragment shader — fades the trail buffer.
 *
 * The trail buffer is 8-bit RGBA, so each fade step has to re-quantize to a
 * byte. The original Agafonkin shader used floor(255*c*opacity)/255, but floor
 * biases every step *downward* — a faint trail pixel loses ~0.5/255 of extra
 * brightness on top of the intended fade each frame, so dim tails snap to zero
 * after only a few frames (they look like specks instead of streaks). Rounding
 * (floor(x + 0.5)) removes that systematic bias, so a pixel fades on the
 * intended fadeOpacity curve and the tails persist their full natural length
 * even at lower fadeOpacity values.
 *
 * ALPHA-ONLY FADE: we decay only the alpha channel each frame and leave RGB
 * untouched. The previous version multiplied the whole color (RGB included) by
 * opacity, which drove an aging tail toward black — so a light-gray head left a
 * tail that darkened as it receded instead of staying the same color. Holding
 * RGB constant and fading alpha means every point along the tail keeps the head
 * color (e.g. the 208,208,208 streamline gray) and simply becomes more
 * transparent with age, so the whole streamline reads as one consistent color.
 * The composite over the map uses non-premultiplied SRC_ALPHA blending
 * (see compositeToMap), which composites that constant RGB at the decaying
 * alpha correctly. */
const SCREEN_FRAG = `
precision mediump float;

uniform sampler2D u_screen;
uniform float u_opacity;

varying vec2 v_tex_pos;

void main() {
    vec4 color = texture2D(u_screen, 1.0 - v_tex_pos);
    float a = floor(255.0 * color.a * u_opacity + 0.5) / 255.0;
    gl_FragColor = vec4(color.rgb, a);
}
`;

/** UPDATE fragment shader — advects particles in wind-UV space. */
const UPDATE_FRAG = `
precision highp float;

uniform sampler2D u_particles;
uniform sampler2D u_wind;
uniform vec2 u_wind_res;
uniform vec2 u_wind_min;
uniform vec2 u_wind_max;
uniform float u_rand_seed;
uniform float u_speed_factor;
uniform float u_speed_scale;
uniform float u_drop_rate;
uniform float u_drop_rate_bump;
// Visible viewport expressed in particle-UV space (a sub-rect of [0,1]^2 of
// the wind data extent). Respawned particles are scattered inside THIS rect
// rather than across the whole data extent, so the fixed particle budget
// concentrates wherever the camera is looking. At low zoom the rect ~= the
// full [0,1] square (no change); as you zoom in it shrinks, which is what
// keeps on-screen streamline density roughly constant across zoom levels.
// Padded slightly beyond the screen edges by the caller so particles can flow
// in from just off-screen instead of popping in at the border.
uniform vec2 u_view_min;
uniform vec2 u_view_max;

varying vec2 v_tex_pos;

const vec3 rand_constants = vec3(12.9898, 78.233, 4375.85453);
float rand(const vec2 co) {
    float t = dot(rand_constants.xy, co);
    return fract(sin(t) * (rand_constants.z + t));
}

vec2 lookup_wind(const vec2 uv) {
    vec2 px = 1.0 / u_wind_res;
    vec2 vc = (floor(uv * u_wind_res)) * px;
    vec2 f = fract(uv * u_wind_res);
    vec2 tl = texture2D(u_wind, vc).rg;
    vec2 tr = texture2D(u_wind, vc + vec2(px.x, 0)).rg;
    vec2 bl = texture2D(u_wind, vc + vec2(0, px.y)).rg;
    vec2 br = texture2D(u_wind, vc + px).rg;
    return mix(mix(tl, tr, f.x), mix(bl, br, f.x), f.y);
}

void main() {
    vec4 color = texture2D(u_particles, v_tex_pos);
    // Particle position is stored in VIEWPORT space: [0,1]^2 spans the visible
    // map viewport (padded), NOT the whole data extent. This is what makes the
    // simulation zoom-independent — the particles always fill the screen and
    // advection is measured in screen fractions, so density and motion look the
    // same at every zoom. We map viewport-space → data-UV only to sample wind.
    vec2 pos = vec2(
        color.r / 255.0 + color.b,
        color.g / 255.0 + color.a);

    // Viewport-space → data-UV, to look up the wind velocity at this particle.
    vec2 dataUv = mix(u_view_min, u_view_max, pos);
    // Dequantize the 1..255 GFS byte encoding ((byte-1)/254, byte 0 = NoData)
    // so byte 128 decodes to exactly 0 m/s — see the draw shader note.
    vec2 windT = clamp((lookup_wind(dataUv) * 255.0 - 1.0) / 254.0, 0.0, 1.0);
    vec2 velocity = mix(u_wind_min, u_wind_max, windT);
    float speed_t = length(velocity) / length(u_wind_max);

    // Advection. velocity is in data units; convert to a step in data-UV
    // (the 0.0001 constant + speedFactor), then express it in VIEWPORT space by
    // dividing by the view span (data-UV per viewport unit). The net effect:
    // particles advance the same fraction of the SCREEN per frame regardless of
    // zoom, so they neither freeze when zoomed in (old bug: step was a fraction
    // of the whole extent and went sub-pixel) nor teleport.
    float distortion = cos(radians(dataUv.y * 180.0 - 90.0));
    vec2 viewSpan = max(u_view_max - u_view_min, vec2(1e-6));
    // u_speed_scale lets the app dial the per-frame step down at close zoom.
    // Advection is otherwise zoom-independent in SCREEN terms, but because a
    // particle crosses a small geographic area quickly when zoomed in, that
    // reads as "too fast" — so we scale it back as zoom increases.
    vec2 offset = vec2(velocity.x / distortion, -velocity.y)
                  * 0.0001 * u_speed_factor * u_speed_scale / viewSpan;

    pos = pos + offset;

    vec2 seed = (pos + v_tex_pos) * u_rand_seed;

    // A particle that has flowed off-screen (outside the [0,1] viewport) is
    // recycled. Combined with the random respawn below (also in viewport
    // space), this keeps the visible area full at every zoom.
    float outside = step(pos.x, 0.0) + step(1.0, pos.x)
                  + step(pos.y, 0.0) + step(1.0, pos.y);

    float drop_rate = u_drop_rate + speed_t * u_drop_rate_bump;
    float drop = max(step(1.0 - drop_rate, rand(seed)), min(1.0, outside));

    // Random respawn position in VIEWPORT space ([0,1] = the visible screen),
    // so the whole particle budget is always spent on what the user sees.
    vec2 random_pos = vec2(
        rand(seed + 1.3),
        rand(seed + 2.1));
    pos = mix(pos, random_pos, drop);

    gl_FragColor = vec4(
        fract(pos * 255.0),
        floor(pos * 255.0) / 255.0);
}
`;

// ─── GL helpers ─────────────────────────────────────────────────────────────

type ProgramWrapper = {
  program: WebGLProgram;
  [key: string]: WebGLProgram | WebGLUniformLocation | number;
};

function createShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) || "shader compile failed");
  }
  return shader;
}

function createProgram(gl: WebGLRenderingContext, vertexSource: string, fragmentSource: string): ProgramWrapper {
  const program = gl.createProgram()!;
  gl.attachShader(program, createShader(gl, gl.VERTEX_SHADER, vertexSource));
  gl.attachShader(program, createShader(gl, gl.FRAGMENT_SHADER, fragmentSource));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) || "program link failed");
  }

  const wrapper: ProgramWrapper = { program };
  const numAttributes = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES) as number;
  for (let i = 0; i < numAttributes; i++) {
    const a = gl.getActiveAttrib(program, i)!;
    wrapper[a.name] = gl.getAttribLocation(program, a.name);
  }
  const numUniforms = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;
  for (let i = 0; i < numUniforms; i++) {
    const u = gl.getActiveUniform(program, i)!;
    wrapper[u.name] = gl.getUniformLocation(program, u.name)!;
  }
  return wrapper;
}

function createTexture(
  gl: WebGLRenderingContext,
  filter: number,
  data: Uint8Array | TexImageSource,
  width?: number,
  height?: number,
): WebGLTexture {
  const texture = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  if (data instanceof Uint8Array) {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width!, height!, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
  } else {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, data);
  }
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
}

function bindTexture(gl: WebGLRenderingContext, texture: WebGLTexture, unit: number) {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, texture);
}

function createBuffer(gl: WebGLRenderingContext, data: Float32Array): WebGLBuffer {
  const buffer = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  return buffer;
}

function bindAttribute(gl: WebGLRenderingContext, buffer: WebGLBuffer, attribute: number, numComponents: number) {
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.enableVertexAttribArray(attribute);
  gl.vertexAttribPointer(attribute, numComponents, gl.FLOAT, false, 0, 0);
}

function bindFramebuffer(gl: WebGLRenderingContext, framebuffer: WebGLFramebuffer | null, texture?: WebGLTexture) {
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  if (texture) {
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  }
}

// ─── Color ramp ─────────────────────────────────────────────────────────────

// Streamline particle color. Previously a speed ramp (blue→green→yellow→red);
// now a solid light gray (rgb 208,208,208 = #d0d0d0) at every speed so the
// streamlines read as plain light-gray lines over the colored wind raster. All
// stops are the same gray, so the speed_t lookup in the draw fragment shader
// always returns this color regardless of wind magnitude. To restore
// speed-based coloring, replace these with a multi-color ramp again.
const defaultRampColors: Record<string, string> = {
  0.0: "#d0d0d0",
  1.0: "#d0d0d0",
};

function getColorRamp(colors: Record<string, string>): Uint8Array {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  canvas.width = 256;
  canvas.height = 1;
  const gradient = ctx.createLinearGradient(0, 0, 256, 0);
  for (const stop in colors) {
    gradient.addColorStop(+stop, colors[stop]);
  }
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 256, 1);
  return new Uint8Array(ctx.getImageData(0, 0, 256, 1).data.buffer);
}

// ─── Pending data buffered before onAdd() ────────────────────────────────────

type WindData = {
  image: HTMLCanvasElement;
  width: number;
  height: number;
  uMin: number;
  uMax: number;
  vMin: number;
  vMax: number;
};

type PendingFrame = {
  windData: WindData;
  bbox: number[];
};

// ─── Custom layer (drawing happens in MapLibre's GL context) ─────────────────

export interface WindStreamlinesOptions {
  id?: string;
  numParticles?: number;
  /** Size of the particle state texture (NxN). numParticles = N*N. */
  particleStateSize?: number;
  fadeOpacity?: number;
  speedFactor?: number;
  dropRate?: number;
  dropRateBump?: number;
  windRange?: [number, number];
  /** Initial overall opacity multiplier (0..1), applied at composite time. */
  opacity?: number;
  pointSize?: number;
  /** Initial RGB brightness multiplier (0..1) for the streamline color. The
   *  app passes a lower value on the dark basemap so a freshly-built layer
   *  renders at the right darkness immediately (no full-brightness flash before
   *  the live setter runs). Defaults to 1.0. */
  colorBrightness?: number;
}

export class WeatherWindStreamlines implements CustomLayerInterface {
  // ── CustomLayerInterface required fields ──
  readonly id: string;
  readonly type = "custom" as const;
  readonly renderingMode = "2d" as const;

  // Tunables (mirror previous public surface)
  fadeOpacity: number;
  speedFactor: number;
  dropRate: number;
  dropRateBump: number;
  /** Fraction of particles to draw (0..1). Driven by zoom from the app to thin
   *  the field at close zoom without a rebuild. Defaults to 1 (draw all). */
  private drawFraction = 1;
  /** Multiplier on the advection step (0..1+). Driven by zoom to slow motion
   *  at close zoom. Defaults to 1 (no change). */
  private speedScale = 1;
  /** Multiplier on the streamline RGB (alpha untouched), driven by the basemap
   *  theme. 1.0 = the base color as-authored (light map); the app sets a lower
   *  value on the dark map so the streamlines render darker there. Defaults to
   *  1.0 so behavior is unchanged until the app drives it. */
  private colorBrightness = 1;
  private windRange: [number, number];
  private pointSize: number;
  private requestedParticles: number;
  private _opacity: number;
  private _visible = true;

  // GL state — populated in onAdd()
  private map: MaplibreMap | null = null;
  private gl: WebGLRenderingContext | null = null;

  private drawProgram: ProgramWrapper | null = null;
  private screenProgram: ProgramWrapper | null = null;
  private updateProgram: ProgramWrapper | null = null;

  private quadBuffer: WebGLBuffer | null = null;
  private framebuffer: WebGLFramebuffer | null = null;

  private colorRampTexture: WebGLTexture | null = null;
  private backgroundTexture: WebGLTexture | null = null;
  private screenTexture: WebGLTexture | null = null;
  private screenW = 0;
  private screenH = 0;

  private particleStateResolution = 0;
  private numParticlesActual = 0;
  private particleStateTexture0: WebGLTexture | null = null;
  private particleStateTexture1: WebGLTexture | null = null;
  private particleIndexBuffer: WebGLBuffer | null = null;

  private windData: WindData | null = null;
  private windTexture: WebGLTexture | null = null;
  private windBbox: number[] | null = null;
  private windBboxMerc = new Float32Array(4);
  /** Visible viewport as a sub-rect of the wind data extent, in particle-UV
   *  space: [minU, minV, maxU, maxV]. Recomputed each frame from the map
   *  bounds and fed to the update shader so particles respawn where the camera
   *  is looking (keeps streamline density roughly constant across zoom). */
  private viewUv = new Float32Array([0, 0, 1, 1]);

  // Frames may arrive via setWindFrame() before onAdd() runs.
  private pendingFrame: PendingFrame | null = null;

  private onMoveHandler: (() => void) | null = null;
  private trailDirty = true;
  /** MVP from the previous rendered frame, used to detect whether the camera
   *  actually changed. Trails only need clearing when it did. */
  private lastMvp: Float32Array | null = null;

  private disposed = false;

  constructor(opts: WindStreamlinesOptions = {}) {
    this.id = opts.id ?? "weather-wind-streamlines";
    this.fadeOpacity = opts.fadeOpacity ?? 0.996;
    this.speedFactor = opts.speedFactor ?? 0.25;
    this.dropRate = opts.dropRate ?? 0.003;
    this.dropRateBump = opts.dropRateBump ?? 0.01;
    this.windRange = opts.windRange ?? [-70, 70];
    this.pointSize = opts.pointSize ?? 1.0;
    this.colorBrightness = Math.max(0, Math.min(1, opts.colorBrightness ?? 1.0));
    this._opacity = opts.opacity ?? 1.0;
    this.requestedParticles = opts.particleStateSize
      ? opts.particleStateSize * opts.particleStateSize
      : (opts.numParticles ?? 65536);
  }

  // ─── CustomLayerInterface lifecycle ─────────────────────────────────────

  onAdd(map: MaplibreMap, gl: WebGLRenderingContext) {
    this.map = map;
    this.gl = gl;

    // Compile programs.
    this.drawProgram = createProgram(gl, DRAW_VERT, DRAW_FRAG);
    this.screenProgram = createProgram(gl, QUAD_VERT, SCREEN_FRAG);
    this.updateProgram = createProgram(gl, QUAD_VERT, UPDATE_FRAG);

    this.quadBuffer = createBuffer(gl, new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]));
    this.framebuffer = gl.createFramebuffer()!;

    this.colorRampTexture = createTexture(gl, gl.LINEAR, getColorRamp(defaultRampColors), 16, 16);

    // Allocate particle state and trail buffers up front.
    this.allocateParticles(this.requestedParticles);
    this.ensureTrailTextures();

    // NOTE: We deliberately do NOT clear trails on every "move" event. During
    // the load/intro camera animation `move` fires every frame, and clearing
    // here would wipe the accumulated streamlines on each one — the particles
    // would render as a flickering point cloud that never forms streaks. The
    // render() loop compares the projection matrix frame-to-frame and clears
    // only when the camera actually moved, which is the real correctness need.
    this.onMoveHandler = null;

    // Apply any frame that arrived before onAdd().
    if (this.pendingFrame) {
      const pf = this.pendingFrame;
      this.pendingFrame = null;
      this.uploadWindFrame(pf.windData, pf.bbox);
    }
  }

  onRemove() {
    if (this.onMoveHandler && this.map) {
      this.map.off("move", this.onMoveHandler);
    }
    this.onMoveHandler = null;

    const gl = this.gl;
    if (gl) {
      // Best-effort cleanup. We don't bother deleting programs/buffers in
      // detail because MapLibre's context will outlive us anyway.
      if (this.windTexture) gl.deleteTexture(this.windTexture);
      if (this.colorRampTexture) gl.deleteTexture(this.colorRampTexture);
      if (this.backgroundTexture) gl.deleteTexture(this.backgroundTexture);
      if (this.screenTexture) gl.deleteTexture(this.screenTexture);
      if (this.particleStateTexture0) gl.deleteTexture(this.particleStateTexture0);
      if (this.particleStateTexture1) gl.deleteTexture(this.particleStateTexture1);
      if (this.framebuffer) gl.deleteFramebuffer(this.framebuffer);
    }
    this.windTexture = null;
    this.colorRampTexture = null;
    this.backgroundTexture = null;
    this.screenTexture = null;
    this.particleStateTexture0 = null;
    this.particleStateTexture1 = null;
    this.framebuffer = null;
    this.disposed = true;
  }

  /** Called by MapLibre every frame. `args` carries the current projection
   *  matrix; we plumb that straight into the draw shader. */
  render(
    gl: WebGLRenderingContext,
    args: { defaultProjectionData: { mainMatrix: number[] | Float32Array } } | Float32Array | number[],
  ) {
    if (this.disposed) return;
    if (!this._visible) return;
    if (!this.windTexture || !this.windData || !this.windBbox) return;
    if (!this.drawProgram || !this.screenProgram || !this.updateProgram) return;
    if (!this.particleStateTexture0 || !this.particleStateTexture1) return;

    // Accept both old (bare matrix) and new (args.defaultProjectionData)
    // MapLibre render signatures, mirroring weatherRasterLayer.
    const mvp = ((args as any)?.defaultProjectionData?.mainMatrix
                 ?? (args as any)?.matrix
                 ?? args) as Float32Array | number[];

    // Update mercator bbox uniform from the current frame's lng/lat bbox.
    // Cheap to recompute; cached path isn't worth the complexity.
    const nw = MercatorCoordinate.fromLngLat({ lng: this.windBbox[0], lat: this.windBbox[3] });
    const se = MercatorCoordinate.fromLngLat({ lng: this.windBbox[2], lat: this.windBbox[1] });
    this.windBboxMerc[0] = nw.x;
    this.windBboxMerc[1] = nw.y;
    this.windBboxMerc[2] = se.x;
    this.windBboxMerc[3] = se.y;

    // Compute the visible viewport rect (in data-UV) ONCE per frame, before
    // both the draw and update passes, so they agree on where particles live.
    // (Both shaders map viewport-space ↔ data-UV through this rect.)
    this.updateViewUv();

    // Detect a real camera change by diffing this frame's MVP against the
    // last one. Only then do we need to clear the screen-space trail buffer
    // (so streaks don't smear across the wrong geography). When the camera is
    // still — including across rapid same-view repaints during load — the
    // trail is preserved and the streamlines build up as intended.
    if (this.matrixChanged(mvp)) {
      this.trailDirty = true;
    }

    // Resize/clear trail textures if the canvas size changed or a camera
    // change requested a clear. We compare against MapLibre's drawingBufferWidth/
    // Height so DPR is handled naturally.
    this.ensureTrailTextures();
    if (this.trailDirty) {
      this.clearTrailTextures();
      this.trailDirty = false;
    }

    // Remember this frame's matrix so the next frame can detect movement.
    if (!this.lastMvp) this.lastMvp = new Float32Array(16);
    this.lastMvp.set(mvp as ArrayLike<number>);

    // Bind shared textures.
    bindTexture(gl, this.windTexture, 0);
    bindTexture(gl, this.particleStateTexture0, 1);

    // 1) Update screen FBO: faded prior frame + freshly stamped particles.
    this.drawScreen(gl, mvp);

    // 2) Composite the screen FBO into MapLibre's framebuffer (NDC quad).
    this.compositeToMap(gl);

    // 3) Advance particle simulation for next frame.
    this.updateParticles(gl);

    // Clean up state we set so we don't surprise MapLibre's other layers.
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.disable(gl.BLEND);

    // Schedule the next frame so the simulation keeps animating even when
    // the camera is still. MapLibre treats triggerRepaint() as a no-op
    // beyond requesting one more render pass.
    if (this.map) this.map.triggerRepaint();
  }

  // ─── Public API (kept compatible with previous version) ─────────────────

  /** Feed a WeatherFrame (RG wind data). Safe to call before or after onAdd. */
  setWindFrame(frame: WeatherFrame) {
    const src = document.createElement("canvas");
    src.width = frame.width;
    src.height = frame.height;
    const ctx = src.getContext("2d")!;
    const imgData = ctx.createImageData(frame.width, frame.height);
    imgData.data.set(frame.rawData);
    ctx.putImageData(imgData, 0, 0);

    const windData: WindData = {
      image: src,
      width: frame.width,
      height: frame.height,
      uMin: this.windRange[0],
      uMax: this.windRange[1],
      vMin: this.windRange[0],
      vMax: this.windRange[1],
    };

    if (this.gl) {
      this.uploadWindFrame(windData, frame.bbox);
    } else {
      this.pendingFrame = { windData, bbox: frame.bbox };
    }
  }

  setVisible(visible: boolean) {
    this._visible = visible;
    if (visible) this.trailDirty = true; // start fresh on re-show
    if (this.map) this.map.triggerRepaint();
  }

  setOpacity(opacity: number) {
    this._opacity = Math.max(0, Math.min(1, opacity));
    if (this.map) this.map.triggerRepaint();
  }

  setSpeedFactor(s: number) { this.speedFactor = s; }
  setFadeOpacity(f: number) { this.fadeOpacity = Math.max(0, Math.min(1, f)); }
  setDropRate(r: number) { this.dropRate = r; }
  setDropRateBump(r: number) { this.dropRateBump = r; }
  /** Zoom-driven thinning: fraction of particles drawn, 0..1. */
  setDrawFraction(f: number) { this.drawFraction = Math.max(0, Math.min(1, f)); }
  /** Zoom-driven slowdown: multiplier on the advection step. */
  setSpeedScale(s: number) { this.speedScale = Math.max(0, s); }
  /** Live point size (px). Was compile-time; now a uniform so zoom can thicken
   *  the streamlines without rebuilding the layer. */
  setPointSize(p: number) { this.pointSize = Math.max(0.1, p); }
  /** Theme-driven RGB brightness multiplier (alpha untouched). 1.0 = base
   *  color; the app passes a lower value on the dark map to darken the
   *  streamlines there. Clamped to [0, 1] (a value >1 could only push the gray
   *  toward white, which isn't wanted). */
  setColorBrightness(b: number) { this.colorBrightness = Math.max(0, Math.min(1, b)); }

  /** Convenience for callers that want to remove this layer. They should
   *  prefer `map.removeLayer(layer.id)`, which triggers onRemove. */
  dispose() {
    if (this.disposed) return;
    if (this.map) {
      try { this.map.removeLayer(this.id); } catch (_) { /* not added */ }
    }
    // If never added, mark disposed so any pending frame uploads no-op.
    this.disposed = true;
  }

  // ─── Internals ───────────────────────────────────────────────────────────

  /** Recompute `viewUv` — the visible viewport expressed as a sub-rect of the
   *  wind data extent in particle-UV [0,1]² space. The draw shader maps a
   *  particle's UV linearly into the wind bbox in UNIT-MERCATOR space
   *  (mix(nwMerc, seMerc, uv)), so to invert it we take the map's current
   *  bounds, convert the corners to unit-mercator, and express them as a
   *  fraction of the wind bbox's mercator rect. We pad the rect outward so
   *  particles can flow in from just off-screen instead of popping in at the
   *  border, and clamp to [0,1] so we never respawn outside the data. */
  private updateViewUv() {
    const map = this.map;
    if (!map || !this.windBbox) return;

    // Wind bbox in unit-mercator (same corners used for windBboxMerc / the
    // draw shader): NW = (minLng, maxLat), SE = (maxLng, minLat).
    const nwX = this.windBboxMerc[0];
    const nwY = this.windBboxMerc[1];
    const seX = this.windBboxMerc[2];
    const seY = this.windBboxMerc[3];
    const spanX = seX - nwX;
    const spanY = seY - nwY;
    if (spanX === 0 || spanY === 0) return;

    // Current viewport bounds → unit-mercator corners.
    const b = map.getBounds();
    const w = MercatorCoordinate.fromLngLat({ lng: b.getWest(), lat: b.getNorth() });
    const e = MercatorCoordinate.fromLngLat({ lng: b.getEast(), lat: b.getSouth() });

    // Express as a fraction of the wind bbox mercator rect → UV.
    let u0 = (w.x - nwX) / spanX;
    let v0 = (w.y - nwY) / spanY;
    let u1 = (e.x - nwX) / spanX;
    let v1 = (e.y - nwY) / spanY;

    // Order-normalize (defensive — mercator y grows southward, matching our
    // NW→SE convention, but guard anyway).
    if (u1 < u0) { const t = u0; u0 = u1; u1 = t; }
    if (v1 < v0) { const t = v0; v0 = v1; v1 = t; }

    // Pad outward by 15% of the visible span so particles enter from just
    // beyond the edges rather than popping in exactly at the border.
    const padU = (u1 - u0) * 0.15;
    const padV = (v1 - v0) * 0.15;
    u0 -= padU; u1 += padU;
    v0 -= padV; v1 += padV;

    // Clamp to the data extent.
    this.viewUv[0] = Math.max(0, Math.min(1, u0));
    this.viewUv[1] = Math.max(0, Math.min(1, v0));
    this.viewUv[2] = Math.max(0, Math.min(1, u1));
    this.viewUv[3] = Math.max(0, Math.min(1, v1));
  }

  /** True if `mvp` differs meaningfully from the last rendered matrix.
   *
   *  This decides whether to clear the screen-space trail buffer (a clear is
   *  needed on a real camera move so streaks don't smear across the wrong
   *  geography, but must NOT happen on a still camera or the tails never build
   *  up). The comparison MUST be scale-invariant: MapLibre recomputes the
   *  projection matrix every frame, and at close zoom (z ~6–14) its entries are
   *  very large (the mercator→clip scale grows like 2^zoom). The low-order
   *  floating-point bits of numbers that big jitter frame-to-frame by amounts
   *  far exceeding any small absolute epsilon — so an absolute threshold like
   *  1e-9 reports "changed" every single frame at high zoom, wiping the trail
   *  each frame and leaving only scattered points with no tails. A RELATIVE
   *  tolerance (scaled by the magnitude of each entry) treats a perfectly still
   *  camera as unchanged at every zoom level, which is what lets the trails
   *  persist and form streamlines. */
  private matrixChanged(mvp: Float32Array | number[]): boolean {
    if (!this.lastMvp) return true;
    const REL = 1e-6; // relative tolerance; scale-invariant across zoom levels
    for (let i = 0; i < 16; i++) {
      const a = this.lastMvp[i];
      const b = (mvp as ArrayLike<number>)[i];
      const scale = Math.max(Math.abs(a), Math.abs(b), 1);
      if (Math.abs(a - b) > REL * scale) {
        return true;
      }
    }
    return false;
  }

  private uploadWindFrame(windData: WindData, bbox: number[]) {
    const gl = this.gl;
    if (!gl) return;
    if (this.windTexture) gl.deleteTexture(this.windTexture);
    this.windTexture = createTexture(gl, gl.LINEAR, windData.image);
    this.windData = windData;

    // Never clear the trail just because the wind texture changed. The trail
    // buffer is screen-space — it records where particles *have been* on screen.
    // Uploading a new wind frame (new time-step or bbox) only changes the
    // velocity field that drives future particle movement; it does not change
    // where current particles are or what the screen-to-geography mapping is.
    // Clearing here causes the streamlines to flash back to bare points on
    // every keyframe transition during animation playback.
    //
    // The only legitimate reason to clear the trail is a camera move (which
    // changes the screen-to-geography mapping), and that is handled entirely
    // by the matrixChanged() check in render().
    this.windBbox = bbox;

    if (this.map) this.map.triggerRepaint();
  }

  private allocateParticles(numRequested: number) {
    const gl = this.gl;
    if (!gl) return;
    const particleRes = Math.ceil(Math.sqrt(numRequested));
    this.particleStateResolution = particleRes;
    this.numParticlesActual = particleRes * particleRes;

    const particleState = new Uint8Array(this.numParticlesActual * 4);
    for (let i = 0; i < particleState.length; i++) {
      particleState[i] = Math.floor(Math.random() * 256);
    }
    if (this.particleStateTexture0) gl.deleteTexture(this.particleStateTexture0);
    if (this.particleStateTexture1) gl.deleteTexture(this.particleStateTexture1);
    this.particleStateTexture0 = createTexture(gl, gl.NEAREST, particleState, particleRes, particleRes);
    this.particleStateTexture1 = createTexture(gl, gl.NEAREST, particleState, particleRes, particleRes);

    const particleIndices = new Float32Array(this.numParticlesActual);
    for (let i = 0; i < this.numParticlesActual; i++) particleIndices[i] = i;
    if (this.particleIndexBuffer) gl.deleteBuffer(this.particleIndexBuffer);
    this.particleIndexBuffer = createBuffer(gl, particleIndices);
  }

  /** Resize/allocate the screen-space trail textures to match MapLibre's
   *  drawing buffer. Called every render — cheap when sizes haven't changed. */
  private ensureTrailTextures() {
    const gl = this.gl;
    if (!gl) return;
    const w = gl.drawingBufferWidth;
    const h = gl.drawingBufferHeight;
    if (w === this.screenW && h === this.screenH && this.backgroundTexture && this.screenTexture) {
      return;
    }
    this.screenW = w;
    this.screenH = h;
    const empty = new Uint8Array(w * h * 4);
    if (this.backgroundTexture) gl.deleteTexture(this.backgroundTexture);
    if (this.screenTexture) gl.deleteTexture(this.screenTexture);
    this.backgroundTexture = createTexture(gl, gl.NEAREST, empty, w, h);
    this.screenTexture = createTexture(gl, gl.NEAREST, empty, w, h);
    this.trailDirty = false; // freshly allocated, already zeroed
  }

  private clearTrailTextures() {
    const gl = this.gl;
    if (!gl || !this.backgroundTexture || !this.screenTexture) return;
    const empty = new Uint8Array(this.screenW * this.screenH * 4);
    gl.bindTexture(gl.TEXTURE_2D, this.backgroundTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.screenW, this.screenH, 0, gl.RGBA, gl.UNSIGNED_BYTE, empty);
    gl.bindTexture(gl.TEXTURE_2D, this.screenTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.screenW, this.screenH, 0, gl.RGBA, gl.UNSIGNED_BYTE, empty);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  private drawScreen(gl: WebGLRenderingContext, mvp: Float32Array | number[]) {
    if (!this.framebuffer || !this.screenTexture || !this.backgroundTexture) return;

    bindFramebuffer(gl, this.framebuffer, this.screenTexture);
    gl.viewport(0, 0, this.screenW, this.screenH);

    // Make sure blending doesn't bleed in from MapLibre's prior draws — we
    // want to overwrite the FBO contents.
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.STENCIL_TEST);

    // Faded copy of the previous frame (the "trail").
    this.drawTexture(gl, this.backgroundTexture, this.fadeOpacity);
    // Then stamp this frame's particles on top, projected through MapLibre's
    // matrix so they sit on the correct map locations.
    this.drawParticles(gl, mvp);

    // Swap so next frame's "background" is what we just drew.
    const tmp = this.backgroundTexture;
    this.backgroundTexture = this.screenTexture;
    this.screenTexture = tmp;
  }

  private compositeToMap(gl: WebGLRenderingContext) {
    if (!this.backgroundTexture) return;

    // Render to MapLibre's default framebuffer at the full canvas size.
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.STENCIL_TEST);

    // backgroundTexture is the freshly-drawn frame after the swap above.
    this.drawTexture(gl, this.backgroundTexture, this._opacity);
  }

  private drawTexture(gl: WebGLRenderingContext, texture: WebGLTexture, opacity: number) {
    const program = this.screenProgram!;
    gl.useProgram(program.program);

    bindAttribute(gl, this.quadBuffer!, program.a_pos as number, 2);
    bindTexture(gl, texture, 2);
    gl.uniform1i(program.u_screen as WebGLUniformLocation, 2);
    gl.uniform1f(program.u_opacity as WebGLUniformLocation, opacity);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private drawParticles(gl: WebGLRenderingContext, mvp: Float32Array | number[]) {
    const program = this.drawProgram!;
    gl.useProgram(program.program);

    bindAttribute(gl, this.particleIndexBuffer!, program.a_index as number, 1);
    bindTexture(gl, this.colorRampTexture!, 2);

    gl.uniform1i(program.u_wind as WebGLUniformLocation, 0);
    gl.uniform1i(program.u_particles as WebGLUniformLocation, 1);
    gl.uniform1i(program.u_color_ramp as WebGLUniformLocation, 2);

    gl.uniform1f(program.u_particles_res as WebGLUniformLocation, this.particleStateResolution);
    gl.uniform2f(program.u_wind_min as WebGLUniformLocation, this.windData!.uMin, this.windData!.vMin);
    gl.uniform2f(program.u_wind_max as WebGLUniformLocation, this.windData!.uMax, this.windData!.vMax);

    // Geo-projection uniforms — MapLibre's matrix carries tilt/rotation.
    gl.uniformMatrix4fv(
      program.u_mvp as WebGLUniformLocation,
      false,
      mvp as Float32Array,
    );
    gl.uniform4fv(
      program.u_windBboxMerc as WebGLUniformLocation,
      this.windBboxMerc,
    );
    // View rect (data-UV) so the draw shader can map viewport-space particle
    // positions back into data-UV → mercator. Must use the SAME rect the update
    // shader used this frame; updateViewUv() is called in updateParticles()
    // (which runs before this), so this.viewUv is current.
    gl.uniform2f(program.u_view_min as WebGLUniformLocation, this.viewUv[0], this.viewUv[1]);
    gl.uniform2f(program.u_view_max as WebGLUniformLocation, this.viewUv[2], this.viewUv[3]);
    gl.uniform1f(program.u_draw_fraction as WebGLUniformLocation, this.drawFraction);
    gl.uniform1f(program.u_point_size as WebGLUniformLocation, this.pointSize);
    gl.uniform1f(program.u_brightness as WebGLUniformLocation, this.colorBrightness);

    gl.drawArrays(gl.POINTS, 0, this.numParticlesActual);
  }

  private updateParticles(gl: WebGLRenderingContext) {
    if (!this.windData || !this.framebuffer || !this.particleStateTexture1) return;

    bindFramebuffer(gl, this.framebuffer, this.particleStateTexture1);
    gl.viewport(0, 0, this.particleStateResolution, this.particleStateResolution);
    gl.disable(gl.BLEND);

    const program = this.updateProgram!;
    gl.useProgram(program.program);

    bindAttribute(gl, this.quadBuffer!, program.a_pos as number, 2);

    gl.uniform1i(program.u_wind as WebGLUniformLocation, 0);
    gl.uniform1i(program.u_particles as WebGLUniformLocation, 1);

    gl.uniform1f(program.u_rand_seed as WebGLUniformLocation, Math.random());
    gl.uniform2f(program.u_wind_res as WebGLUniformLocation, this.windData.width, this.windData.height);
    gl.uniform2f(program.u_wind_min as WebGLUniformLocation, this.windData.uMin, this.windData.vMin);
    gl.uniform2f(program.u_wind_max as WebGLUniformLocation, this.windData.uMax, this.windData.vMax);
    gl.uniform1f(program.u_speed_factor as WebGLUniformLocation, this.speedFactor);
    gl.uniform1f(program.u_speed_scale as WebGLUniformLocation, this.speedScale);
    gl.uniform1f(program.u_drop_rate as WebGLUniformLocation, this.dropRate);
    gl.uniform1f(program.u_drop_rate_bump as WebGLUniformLocation, this.dropRateBump);

    // Viewport rect (UV space) so particles respawn where the camera looks.
    // (Computed once at the top of render() so draw + update agree this frame.)
    gl.uniform2f(program.u_view_min as WebGLUniformLocation, this.viewUv[0], this.viewUv[1]);
    gl.uniform2f(program.u_view_max as WebGLUniformLocation, this.viewUv[2], this.viewUv[3]);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Ping-pong particle textures.
    const tmp = this.particleStateTexture0;
    this.particleStateTexture0 = this.particleStateTexture1;
    this.particleStateTexture1 = tmp;
  }
}